import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { dashboardApi } from '../api/dashboardApi';
import {
  ComponentTimelineEntry,
  MonitoredSite,
  OrganizationGroup,
  MonitoringStats,
  MonitoringEvent,
  ChecksSummary,
} from '../types/Monitoring';
import {
  AlertTriangle,
  Building2,
  Camera,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Server,
  Wifi,
  WifiOff,
} from 'lucide-react';
import './SiteMonitoringPage.css';
import MonitoringAnalyticsPanel from './MonitoringAnalyticsPanel';

const STATUS_LABELS: Record<string, string> = {
  healthy: 'Healthy',
  degraded: 'Degraded',
  critical: 'Critical',
  offline: 'Offline',
  unknown: 'Unknown',
};

const COMPONENT_LABELS: Record<string, string> = {
  frontend: 'UI',
  backend_api: 'API',
  mongodb: 'MongoDB',
  qdrant: 'Qdrant',
  docker: 'Docker',
  wsl: 'WSL / Host',
  workers: 'Workers',
};

const formatTimelineUtc = (isoUtc?: string | null): string => {
  if (!isoUtc) return '';
  const normalized = isoUtc.endsWith('Z') ? isoUtc : `${isoUtc}Z`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return isoUtc;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatComponentTimeline = (entry?: ComponentTimelineEntry): string => {
  if (!entry) return '';
  const since = entry.status_since_display || entry.up_since || entry.down_since || entry.status_since;
  if (!since) return '';
  const label = entry.status_label || entry.status;
  if (label === 'down' || entry.status === 'down') {
    return `Down since ${formatTimelineUtc(entry.down_since || since)}`;
  }
  if (label === 'degraded' || entry.status === 'degraded') {
    return `Degraded since ${formatTimelineUtc(since)}`;
  }
  return `Up since ${formatTimelineUtc(entry.up_since || since)}`;
};

const getCheckStatus = (summary: ChecksSummary, key: string): string => {
  const value = summary[key as keyof ChecksSummary];
  return typeof value === 'string' ? value : 'unknown';
};

const worstStatus = (org: OrganizationGroup): string => {
  if (org.critical > 0) return 'critical';
  if (org.offline > 0) return 'offline';
  if (org.degraded > 0) return 'degraded';
  return 'healthy';
};

const SiteMonitoringPage: React.FC = () => {
  const [stats, setStats] = useState<MonitoringStats | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationGroup[]>([]);
  const [events, setEvents] = useState<MonitoringEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState('');
  const [orgFilter, setOrgFilter] = useState('');
  const [selectedSite, setSelectedSite] = useState<MonitoredSite | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showInsights, setShowInsights] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, sitesRes, eventsRes] = await Promise.all([
        dashboardApi.getMonitoringStats(),
        dashboardApi.getMonitoredSites({
          org_name: orgFilter || undefined,
          status_filter: statusFilter || undefined,
        }),
        dashboardApi.getMonitoringEvents({ org_name: orgFilter || undefined, limit: 20 }),
      ]);
      setStats(statsRes);
      setOrganizations(sitesRes.organizations || []);
      setEvents(eventsRes.events || []);
    } catch (err) {
      console.error('Failed to load monitoring data:', err);
    } finally {
      setLoading(false);
    }
  }, [orgFilter, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, loadData]);

  const orgNames = useMemo(() => organizations.map((o) => o.org_name).sort(), [organizations]);

  const toggleOrg = (org: string) => {
    setExpandedOrgs((prev) => {
      const next = new Set(prev);
      if (next.has(org)) next.delete(org);
      else next.add(org);
      return next;
    });
  };

  const statusClass = (status: string) => `sm-status sm-status--${status}`;

  const formatLastSeen = (site: MonitoredSite) => {
    if (!site.last_seen) return 'Never';
    if (site.minutes_since_heartbeat != null) {
      const m = site.minutes_since_heartbeat;
      if (m < 1) return 'Just now';
      if (m < 60) return `${Math.round(m)}m ago`;
      return `${Math.round(m / 60)}h ago`;
    }
    return dashboardApi.formatDate(site.last_seen);
  };

  const formatEventTime = (timestamp: string) => {
    try {
      const normalized = timestamp.endsWith('Z') ? timestamp : `${timestamp}Z`;
      return new Date(normalized).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dashboardApi.formatDate(timestamp);
    }
  };

  const getEventOrgLabel = (ev: MonitoringEvent) => {
    const org = ev.org_name?.trim();
    if (org) return org;
    const site = ev.site_name?.trim();
    if (site && !site.includes('@')) return site;
    return '—';
  };

  const getEventEmail = (ev: MonitoringEvent) => {
    const email = ev.email?.trim();
    if (email) return email;
    const site = ev.site_name?.trim();
    if (site?.includes('@')) return site;
    return '';
  };

  return (
    <div className="sm-page">
      {stats && (
        <div className="sm-stats">
          <div className="sm-stat">
            <span className="sm-stat-value">{stats.total_sites}</span>
            <span className="sm-stat-label">Sites</span>
          </div>
          <div className="sm-stat sm-stat--healthy">
            <span className="sm-stat-value">{stats.status_counts.healthy || 0}</span>
            <span className="sm-stat-label">Healthy</span>
          </div>
          <div className="sm-stat sm-stat--degraded">
            <span className="sm-stat-value">{stats.status_counts.degraded || 0}</span>
            <span className="sm-stat-label">Degraded</span>
          </div>
          <div className="sm-stat sm-stat--critical">
            <span className="sm-stat-value">{stats.status_counts.critical || 0}</span>
            <span className="sm-stat-label">Critical</span>
          </div>
          <div className="sm-stat sm-stat--offline">
            <span className="sm-stat-value">{stats.status_counts.offline || 0}</span>
            <span className="sm-stat-label">Offline</span>
          </div>
          <div className="sm-stat">
            <span className="sm-stat-value">{stats.organizations}</span>
            <span className="sm-stat-label">Orgs</span>
          </div>
        </div>
      )}

      <div className="sm-toolbar">
        <select value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)} className="sm-select">
          <option value="">All organizations</option>
          {orgNames.map((org) => (
            <option key={org} value={org}>{org}</option>
          ))}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="sm-select">
          <option value="">All statuses</option>
          <option value="healthy">Healthy</option>
          <option value="degraded">Degraded</option>
          <option value="critical">Critical</option>
          <option value="offline">Offline</option>
        </select>
        <span className="sm-toolbar-note">Edge heartbeat · grouped by org</span>
        <div className="sm-toolbar-actions">
          <label className="sm-auto-refresh">
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            Auto 30s
          </label>
          <button type="button" className="sm-btn" onClick={() => setShowInsights((v) => !v)}>
            {showInsights ? 'Hide insights' : 'Insights'}
          </button>
          <button type="button" className="sm-btn sm-btn--primary" onClick={loadData} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'sm-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {showInsights && (
        <div className="sm-insights-wrap">
          <MonitoringAnalyticsPanel compact />
        </div>
      )}

      <div className="sm-main">
        <div className="sm-panel">
          <div className="sm-panel-head">
            <h2 className="sm-panel-title"><Building2 size={14} /> Organizations</h2>
            <span className="sm-panel-sub">{organizations.length} orgs</span>
          </div>
          <div className="sm-panel-body">
            {loading && organizations.length === 0 ? (
              <div className="sm-empty">Loading…</div>
            ) : organizations.length === 0 ? (
              <div className="sm-empty">No sites registered</div>
            ) : (
              organizations.map((org) => (
                <div key={org.org_name} className="sm-org">
                  <button type="button" className="sm-org-head" onClick={() => toggleOrg(org.org_name)}>
                    {expandedOrgs.has(org.org_name) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <span className="sm-org-name">{org.org_name}</span>
                    <span className={statusClass(worstStatus(org))}>
                      {STATUS_LABELS[worstStatus(org)]}
                    </span>
                    <span className="sm-org-meta">{org.site_count} site{org.site_count === 1 ? '' : 's'}</span>
                  </button>
                  {expandedOrgs.has(org.org_name) && (
                    <div className="sm-sites">
                      {org.sites.map((site) => (
                        <button
                          key={site.fingerprint}
                          type="button"
                          className={`sm-site ${selectedSite?.fingerprint === site.fingerprint ? 'sm-site--active' : ''}`}
                          onClick={() => setSelectedSite(site)}
                        >
                          <span className={statusClass(site.overall_status)}>
                            {site.overall_status === 'offline' ? <WifiOff size={12} /> : <Wifi size={12} />}
                          </span>
                          <span className="sm-site-name">{site.site_name || site.email}</span>
                          <span className="sm-site-time">{formatLastSeen(site)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="sm-panel sm-panel--detail">
          {selectedSite ? (
            <>
              <div className="sm-detail-head">
                <h2 className="sm-detail-title">{selectedSite.site_name || selectedSite.email}</h2>
                <p className="sm-detail-sub">{selectedSite.org_name} · {selectedSite.email}</p>
                <div className="sm-detail-row">
                  <span className={statusClass(selectedSite.overall_status)}>
                    {STATUS_LABELS[selectedSite.overall_status]}
                  </span>
                  <span className="sm-detail-meta">Last seen {formatLastSeen(selectedSite)}</span>
                  {selectedSite.hostname && (
                    <span className="sm-detail-meta">Host {selectedSite.hostname}</span>
                  )}
                </div>
              </div>
              <div className="sm-panel-body sm-detail-body">
                <h3 className="sm-checks-label"><Server size={14} /> Components</h3>
                <div className="sm-checks">
                  {Object.entries(COMPONENT_LABELS).map(([key, label]) => {
                    const status = getCheckStatus(selectedSite.checks_summary, key);
                    const timeline = formatComponentTimeline(selectedSite.component_timeline?.[key]);
                    return (
                      <div key={key} className="sm-check">
                        <div className="sm-check-row">
                          <span>{label}</span>
                          <span className={statusClass(status)}>{status}</span>
                        </div>
                        {timeline && <div className="sm-check-time">{timeline}</div>}
                      </div>
                    );
                  })}
                  <div className="sm-check">
                    <div className="sm-check-row">
                      <span><Camera size={13} /> Cameras</span>
                      <span>{selectedSite.checks_summary.cameras_online ?? '?'} / {selectedSite.checks_summary.cameras_total ?? '?'}</span>
                    </div>
                  </div>
                </div>
                {(selectedSite.checks_summary.cameras_offline?.length ?? 0) > 0 && (
                  <div className="sm-offline-cams">
                    <AlertTriangle size={13} />
                    Offline: {selectedSite.checks_summary.cameras_offline?.join(', ')}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="sm-detail-empty">
              <Building2 size={32} strokeWidth={1.2} />
              <p>Select a site to view heartbeat details</p>
            </div>
          )}
        </div>
      </div>

      <div className="sm-events">
        <div className="sm-events-head">
          <h2 className="sm-panel-title"><AlertTriangle size={14} /> Recent events</h2>
          <span className="sm-panel-sub">{events.length} latest</span>
        </div>
        <div className="sm-events-body">
          {events.length === 0 ? (
            <div className="sm-empty">No recent alerts</div>
          ) : (
            <table className="sm-events-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Organization</th>
                  <th>Event</th>
                </tr>
              </thead>
              <tbody>
                {events.map((ev) => {
                  const orgLabel = getEventOrgLabel(ev);
                  const email = getEventEmail(ev);
                  return (
                    <tr key={ev.id} className={`sm-event sm-event--${ev.severity}`}>
                      <td className="sm-event-time">{formatEventTime(ev.timestamp)}</td>
                      <td>
                        <span className="sm-event-org">{orgLabel}</span>
                        {email && <span className="sm-event-email">{email}</span>}
                      </td>
                      <td className="sm-event-msg">{ev.message}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default SiteMonitoringPage;
